import { useQuery } from '@tanstack/react-query';
import api from 'api';
import classNames from 'classnames';
import CopyIcon from 'icons/Copy.icon';
import { Button, Loader } from 'ncoded-component-library';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import HttpError from 'components/HttpError';
import { useParams } from 'react-router-dom';

import './Checksums.styles.scss';

const checksumFormatter = (value: number) => value.toString().replace('.', '');

type ChecksumsProps = {
  className?: string;
};

const Checksums: React.FC<ChecksumsProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();

  const { storeNo } = useParams();

  const classes = useMemo(() => classNames(className, 'checksum'), [className]);

  const copyToClipboardAsync = useCallback(
    (text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(function () {
          toast.success(t('Checksums.copiedToClipboard'));
        })
        .catch(function () {
          toast.error(t('Checksums.copiedToClipboardError'));
        });
    },
    [t],
  );

  const {
    isLoading,
    error,
    refetch,
    data: checksumData,
  } = useQuery({
    queryKey: ['checksums', storeNo],
    enabled: !!storeNo,
    queryFn: () => api.admins.getChecksums(storeNo && +storeNo),
  });

  if (error) {
    return <HttpError error={error} onTryAgain={refetch} />;
  }

  return (
    <div className={classes}>
      <h1>{t('Checksums.checksums')}</h1>

      <dl className="checksum__list">
        <div className="checksum__list__item">
          <dt>{t('storeNumber')}</dt>
          <dd>
            {!storeNo ? (
              '-'
            ) : isLoading ? (
              <Loader inline color="#007cb9" />
            ) : (
              checksumData?.store_no
            )}
          </dd>
        </div>
        <div className="checksum__list__item">
          <dt>{t('count')}</dt>
          <dd>
            {!storeNo ? (
              '-'
            ) : isLoading ? (
              <Loader inline color="#007cb9" />
            ) : (
              <>{checksumData?.cnt}</>
            )}
          </dd>
        </div>
        <div className="checksum__list__item checksum__price-sum-item">
          <dt>{t('Checksums.priceSum')}</dt>
          <dd>
            {!storeNo ? (
              '-'
            ) : isLoading ? (
              <Loader inline color="#007cb9" />
            ) : (
              <>
                <Button
                  variant="icon"
                  className="copy-btn"
                  title="copy"
                  onClick={() =>
                    copyToClipboardAsync(
                      checksumFormatter(checksumData?.priceSum),
                    )
                  }
                >
                  <CopyIcon />
                </Button>
                <span>{checksumFormatter(checksumData?.priceSum)}</span>
              </>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default Checksums;
